import { React, useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import { toast } from 'react-toastify';
import { ReactComponent as CancelSvg } from 'assets/images/icons/close_24px.svg';
import { ReactComponent as SaveIcon } from 'assets/images/icons/save-1.svg';
import { ClipLoader } from 'react-spinners';
import { InputComponent } from 'component/Input';
import { TextareaComponent } from 'component/Textarea';
import { SelectComponent } from 'component/Select';
import { ButtonComponent } from 'component/Button';
import { SwitherComponent } from 'component/Swither';
import { axiosWrapper } from 'utils/Helper';
import { getCustomerById, updateCustomer, getOrganizations } from 'api/CustomersApi';

function buildOrganizationOptions(list) {
  const organizations = Array.isArray(list) ? list : [];
  return organizations.map((organization) => {
    const label =
      organization.full_name_uk?.trim() ||
      organization.name ||
      organization.abbreviation ||
      String(organization.id);
    return { value: organization.id, label };
  });
}

function buildSelectedFromCustomer(organizationsDetail) {
  const list = organizationsDetail ?? [];
  const arr = Array.isArray(list) ? list : [];
  return arr.map((organization) => ({
    value: organization?.id,
    label:
      organization?.full_name_uk?.trim() ||
      organization?.name ||
      organization?.abbreviation ||
      String(organization?.id),
  }));
}

export function CustomerDialog({ mode, customerId, onSuccess, triggerIcon }) {
  const [open, setOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);

  const isView = mode === 'view';
  const isEdit = mode === 'edit';

  const loadOrganizations = () => {
    axiosWrapper(() => {
      getOrganizations()
        .then((res) => {
          const list = res?.data?.results ?? res?.data;
          setOrganizationOptions(buildOrganizationOptions(list));
        })
        .catch(() => setOrganizationOptions([]));
    });
  };

  const loadCustomer = () => {
    if (!customerId) return;
    setLoader(true);
    getCustomerById(customerId)
      .then((res) => {
        const customer = res?.data;
        setName(customer?.name || '');
        setDescription(customer?.description != null ? String(customer?.description) : '');
        setIsActive(customer?.is_active !== undefined ? customer?.is_active : true);
        setSelectedOrganizations(
          buildSelectedFromCustomer(customer?.organizations_detail ?? customer?.organizations)
        );
      })
      .catch(() => toast.error('Failed to load customer'))
      .finally(() => setLoader(false));
  };

  const handleOpen = () => {
    loadOrganizations();
    if (customerId) loadCustomer();
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSave = () => {
    if (!name?.trim()) {
      toast.warning('Customer name is required');
      return;
    }
    if (!selectedOrganizations?.length) {
      toast.warning('Select at least one organization');
      return;
    }
    setLoader(true);
    const body = {
      name: name.trim(),
      is_active: isActive,
      description: description.trim() || undefined,
      organizations: selectedOrganizations.map((option) => option.value),
    };
    updateCustomer(customerId, body)
      .then(() => {
        toast.success('Customer updated');
        handleClose();
        onSuccess?.();
      })
      .catch((err) => {
        const message = err?.response?.data?.detail || 'Customer not updated';
        toast.error(message);
      })
      .finally(() => setLoader(false));
  };

  const dialogTitle = isEdit ? 'Edit customer' : 'Customer details';

  return (
    <>
      <button type="button" className="icon-action-btn" onClick={handleOpen} title={dialogTitle}>
        {triggerIcon}
      </button>
      <Dialog open={open} onClose={handleClose} aria-labelledby="customer-dialog-title">
        <div className="confirm-popup customer-form-popup">
          <div className="header">
            <span className="title" id="customer-dialog-title">
              {dialogTitle}
            </span>
            <CancelSvg className="closeBtn" onClick={handleClose} />
          </div>
          {loader && isView ? (
            <div className="flex-column-group" style={{ padding: '24px', alignItems: 'center' }}>
              <ClipLoader color="rgba(0, 177, 183, 1)" size={32} />
            </div>
          ) : (
            <div className="flex-column-group" style={{ padding: '0 24px 24px' }}>
              <InputComponent
                id="customer-dialog-name"
                name="name"
                label="Customer name"
                placeholder="Customer name"
                value={name}
                handleInput={isView ? undefined : setName}
                classNameWrapper="align-left w-100"
                disabled={isView}
              />
              <div className="input-wrapper align-left w-100" style={{ marginTop: '10px' }}>
                <div className="labels">
                  <label htmlFor="textarea-customer-dialog-users-detail">Description</label>
                </div>
                <div className="textarea-wrapper w-100">
                  <TextareaComponent
                    id="customer-dialog-users-detail"
                    name="description"
                    placeholder="Description"
                    value={description}
                    handleTextarea={isView ? () => {} : setDescription}
                    disabled={isView}
                    minRows={1}
                    maxRows={20}
                    style={{ width: '100%', boxSizing: 'border-box', minHeight: 'unset' }}
                  />
                </div>
              </div>
              <div className="org-dialog-status-row" style={{ marginTop: '10px' }}>
                <SwitherComponent
                  checked={isActive}
                  handleSwitcher={isView ? () => {} : setIsActive}
                  labelLeft="Inactive"
                  labelRight="Active"
                />
              </div>
              <div className="select-wrapper w-100" style={{ marginTop: '10px' }}>
                <span className="label">Organizations</span>
                <SelectComponent
                  className="w-100"
                  placeholder="Select organizations..."
                  value={selectedOrganizations}
                  options={organizationOptions}
                  handleSelect={(selected) =>
                    setSelectedOrganizations(Array.isArray(selected) ? selected : [])
                  }
                  isMulti
                  isDisabled={isView}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  noOptionsMessage={() => 'No organizations'}
                  customStyles={{
                    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                    menu: (base) => ({ ...base, zIndex: 2000 }),
                  }}
                />
              </div>
              {isEdit && (
                <div className="buttons-group" style={{ marginTop: '16px' }}>
                  <ButtonComponent
                    iconLeft={<CancelSvg />}
                    className="danger"
                    handleButton={handleClose}
                    label="Cancel"
                  />
                  <ButtonComponent
                    iconLeft={
                      loader ? <ClipLoader color="rgba(0, 177, 183, 1)" size={20} /> : <SaveIcon />
                    }
                    handleButton={handleSave}
                    label="Save"
                    disabled={loader}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}
